import React from "react"

import styled from "styled-components"

import { useLocalization } from "@izettle/lingo-client"
import {
  typography,
  colors,
  borderRadius,
  spacing,
  IconSimpleArrowRightMedium,
  spacingNumeric
} from "@izettle/otto"
import { SvgIcon } from "@izettle/react"

import { FileSelector } from "@/components/file-selector"
import { baseUrl } from "@/constants"
import { useThemeInfo } from "@/hooks/use-theme-info"
import { SelectFilesProps } from "@/models"
import { theme } from "@/theme"

import { DocumentProps } from "./types"

const DocumentContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${spacing.xSmall} ${spacing.none};
`

const LeftInnerWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex-wrap: wrap;
`

const DocumentTitle = styled.span`
  flex: 1;
  ${typography.regular}
  text-align: left;
  margin-right: ${spacing.small};
`

const DocumentText = styled.span`
  flex: 1;
  ${typography.small}
  text-align: left;
  margin-right: ${spacing.small};
`

const DocumentTag = styled.span`
  ${typography.mini}
  display: inline;
  padding: ${spacing.xxSmall};
  margin-right: ${spacing.small};
  background-color: ${colors.background.groupedPrimary};
  border-radius: ${borderRadius.medium};
  width : ${2*spacingNumeric.huge}px;

  @media ${theme.devices.tinyMobile} {
    display: none;
  }

  @media ${theme.devices.mobile} {
    display: inline;
  }
`

const ArrowIcon = styled(SvgIcon).attrs(() => ({
  icon: IconSimpleArrowRightMedium,
}))<{ paypalTheme : boolean}>`
  margin: ${spacing.none} ${spacing.none} ${spacing.none} auto;
  color: ${({paypalTheme}) => paypalTheme ? colors.icon.secondary : colors.icon.action }; 
`

const Wrapper = styled.div`
&& {
  margin-bottom: ${spacing.xxSmall};
  border: 1px solid;
  border-color: ${colors.border.form};
  border-radius: ${borderRadius.medium} !important;

  &:hover {
    border-color: ${colors.border.focus};
  }

  &:active {
    border-color: ${colors.background.inverted} !important;
  }

  button{
    &:after{
      border-style: none !important;
    }
  }
}
`

type DocumentItemProps = {
  document: DocumentProps
  isRecommended?: boolean
  selectFiles: SelectFilesProps
}

export const DocumentItem: React.FC<DocumentItemProps> = ({
  document,
  isRecommended = false,
  selectFiles,
}) => {
  const { text } = useLocalization()
  const {paypalTheme} = useThemeInfo()
  const redirectURL = document.doubleSided
    ? `${baseUrl}/proofOfIdentity/upload-front`
    : `${baseUrl}/proofOfIdentity/upload`

  return (
    <Wrapper>
      <FileSelector
        name={document.purpose}
        type={document.type}
        selectFiles={selectFiles}
        redirect={redirectURL}
        isDoubleSided={document.doubleSided}
        description={document.description}
        variant="secondary"
        size="small"
      >
        <DocumentContentWrapper>
          <LeftInnerWrapper>
            <DocumentTitle>{text(document.title)}</DocumentTitle>
            {document.doubleSided && (
              <DocumentText>{text("PROOF_OF_IDENTITY_DOCUMENT_TYPE_NATIONAL_ID_DESCRIPTION")}</DocumentText>
            )}
          </LeftInnerWrapper>
          {isRecommended && (
            <DocumentTag>{text("PROOF_OF_IDENTITY_RECOMMENDED_TAG")}</DocumentTag>
          )}
          <ArrowIcon paypalTheme={paypalTheme} />
        </DocumentContentWrapper>
      </FileSelector>
    </Wrapper>
  )
}
